import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { getLocaleData } from '@/selectors/localeSelectors';
import {
  getWalletIsLoading,
  getWalletIsRedirecting,
} from '@/selectors/walletSelector';
import { getIsAuthLoading, getIsLoggedIn } from '@/selectors/authSelectors';
import {
  useOneCmsLocaleCommonContent,
  useOneCmsLocaleProductContent,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlFetchyeHooks';
import {
  useOneCmsLocaleAirports,
  useBdaasAirports,
} from '@/hooks/airportHooks/airportsFetchyeHooks';
import { useIsRedirectingCenturionToLogin } from '@/hooks/forcedLoginHooks';
import { useDevCorsWarning } from '@/hooks/devCorsWarningHook';
import { NotRespondingAlert2 } from './common/NotRespondingAlert';
import Loading from './common/Loading';

const LoungeFinderRequiredDataProvider = ({ children }) => {
  const { error: commonContentError, isLoading: isLoadingCommonContent } =
    useOneCmsLocaleCommonContent();
  const { error: productContentError, isLoading: isLoadingProductContent } =
    useOneCmsLocaleProductContent();
  const { error: oneCmsLocaleAirportsError } = useOneCmsLocaleAirports();
  const { error: bdaasAirportsError } = useBdaasAirports();

  const isRedirectingForcedLogin = useIsRedirectingCenturionToLogin();
  useDevCorsWarning();

  const { locale } = useSelector(getLocaleData);
  const dispatch = useDispatch();
  const isLoadingAuth = useSelector(getIsAuthLoading);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isLoadingWallet = useSelector(getWalletIsLoading);
  const isWalletRedirecting = useSelector(getWalletIsRedirecting);

  useEffect(() => {
    if (commonContentError || productContentError) {
      dispatch(
        addErrorToReport(
          new Error(
            `We could not load the intl to show the page is the users locale language. Showed an error message instead. (Locale: ${locale}.)`
          ),
          {
            collectionMethod: 'IntlWrapper',
            moduleID: 'travel-lounge-finder-root',
          }
        )
      );
    }
  }, [commonContentError, productContentError, dispatch, locale]);

  if (isRedirectingForcedLogin) {
    return (
      <Loading
        size="lg"
        containerClassName="flex flex-justify-center pad margin-t"
      />
    );
  }

  // This data is required to show the page, so we show an error message if it fails to load.
  if (
    commonContentError ||
    productContentError ||
    oneCmsLocaleAirportsError ||
    bdaasAirportsError
  ) {
    return (
      <div className="margin-t">
        <NotRespondingAlert2 />
      </div>
    );
  }

  if (
    isLoadingCommonContent ||
    isLoadingProductContent ||
    isLoadingAuth ||
    (isLoggedIn && isLoadingWallet) ||
    (isLoggedIn && isWalletRedirecting)
  ) {
    return (
      <Loading
        size="lg"
        containerClassName="flex flex-justify-center pad margin-t"
      />
    );
  }

  return children;
};

LoungeFinderRequiredDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LoungeFinderRequiredDataProvider;
